import { h } from "preact";
import { useState, useEffect } from "preact/hooks";
import { CaretDown } from "../../helpers/icons";
import useProduct from "../../helpers/useProduct/useProduct";
import variantHasOptionValue from "../../helpers/variantHasOptionValue";
import resolveInitialVariant from "../../helpers/resolveInitialVariant";
import ProductAddonContent from "./product-addon-content";

export const SIZE_OPTION_NAME = "Size";

export default function ProductAddon({
  productId,
  idPrefix,
  defaultVariantId,
  matchSize,
  selectedVariant,
  setSelectedVariant,
  variantQuantity,
  setVariantQuantity,
  added,
  setAdded,
  addedToCart,
}) {
  const [open, setOpen] = useState(false);
  const [product] = useProduct(productId);

  // The selection is cleared once the main product is added to the cart, so the
  // panel collapses with it rather than sitting open on a reset addon.
  useEffect(() => {
    if (!addedToCart) return;

    setOpen(false);
  }, [addedToCart]);

  if (!product) return null;

  const initialVariantId =
    defaultVariantId ||
    (matchSize
      ? product.variants.find((variant) =>
          variantHasOptionValue({
            variant,
            optionName: SIZE_OPTION_NAME,
            optionValue: matchSize,
          }),
        )?.id
      : null) ||
    null;

  // Falls back to the variant the options will select on load so the image
  // doesn't change when the addon is first opened.
  const defaultVariant = resolveInitialVariant({ product, initialVariantId });

  const image =
    selectedVariant?.image || defaultVariant?.image || product.featuredImage;

  const titleId = `${idPrefix}-title`;
  const panelId = `${idPrefix}-panel`;

  const unitPrice = selectedVariant?.price || {
    amount: product.helpers.price,
    currencyCode: product.helpers.currency,
  };
  const price = window.formatMoney(
    Number(unitPrice.amount) * variantQuantity,
    false,
    unitPrice.currencyCode,
  );
  const showPrice = open || added;

  // Swapped in for the See Details link once the addon is added and collapsed,
  // so the header still says which variant was chosen.
  const variantTitle = product.helpers.hasOnlyDefaultVariant
    ? ""
    : ((selectedVariant || defaultVariant)?.selectedOptions || [])
        .map(({ value }) => value)
        .join(" / ");

  return (
    <div
      className={`product-addon ${open ? "product-addon--open" : ""} ${
        added ? "product-addon--added" : ""
      }`}
    >
      <div className="product-addon__header">
        <button
          type="button"
          className="product-addon__expand"
          aria-expanded={open}
          aria-controls={panelId}
          aria-labelledby={titleId}
          onClick={() => setOpen(!open)}
        ></button>
        {image?.url && (
          <img
            className="product-addon__image"
            src={window.getSizedImageUrl(image.url, "x128")}
            alt={image.altText || product.title}
          />
        )}
        <div className="product-addon__header-content">
          <div className="product-addon__title" id={titleId}>
            {added
              ? product.title
              : window.localeStrings.product.addon_add_title.replace(
                  "[title]",
                  product.title,
                )}
          </div>
          <div className="product-addon__details">
            <a
              href={product.helpers.url}
              className="product-addon__details-cta link link--x-small"
              tabIndex={open ? 0 : -1}
              aria-hidden={!open}
            >
              {window.localeStrings.product.addon_see_details}
            </a>
            {variantTitle && (
              <span
                className="product-addon__variant-title caption-1"
                aria-hidden={open || !added}
              >
                {variantTitle}
              </span>
            )}
          </div>
        </div>
        <div className="product-addon__price" aria-hidden={!showPrice}>
          {added ? `+${price}` : price}
        </div>
        <span className="product-addon__caret">
          <CaretDown />
        </span>
      </div>
      <ProductAddonContent
        open={open}
        id={panelId}
        productId={productId}
        idPrefix={idPrefix}
        initialVariantId={initialVariantId}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
        quantity={variantQuantity}
        setQuantity={setVariantQuantity}
        added={added}
        setAdded={setAdded}
      />
    </div>
  );
}
